import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Search, Clock, ChevronUp, ChevronDown,
  FileText, AlertTriangle, Activity, Calendar,
  CheckSquare, Square, Download, Shield, Eye,
  Inbox, ArrowRight,
  type LucideProps,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { profilerApi } from '@/api/profiler';
import { RiskTierBadge } from '@/components/profiler/RiskTierBadge';
import type { CDDReport, RiskTier, CaseType, CaseStatus, CaseListParams } from '@/types/profiler';

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const delta = Date.now() - new Date(iso).getTime();
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function escapeCsv(val: unknown): string {
  const str = val == null ? '' : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(cases: CDDReport[]): string {
  const headers = ['case_id', 'case_reference', 'entity', 'seed_type', 'case_type', 'risk_tier', 'status', 'client_ref', 'created_at'];
  const rows = cases.map((c) => {
    const entity = c.identifier_cluster?.canonical ?? c.seed ?? c.case_reference ?? '';
    return [
      c.case_id, c.case_reference, entity, c.seed_type ?? '',
      c.case_type ?? 'CDD', c.risk_tier, c.status, c.client_ref ?? '', c.created_at ?? '',
    ].map(escapeCsv).join(',');
  });
  return [headers.join(','), ...rows].join('\n');
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CaseTypeBadge({ type }: { type?: CaseType | string }) {
  const isCDD = type !== 'EDD';
  return (
    <span
      className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full tracking-wider"
      style={{
        background: isCDD ? '#001A2E' : '#1A0030',
        color: isCDD ? '#00C8FF' : '#A855F7',
        border: `1px solid ${isCDD ? '#00C8FF30' : '#A855F730'}`,
      }}
    >
      {isCDD ? 'CDD' : 'EDD'}
    </span>
  );
}

function StatusChip({ status }: { status: CaseStatus }) {
  const cfg: Record<CaseStatus, { label: string; color: string; pulse?: boolean }> = {
    COMPLETE: { label: 'Complete', color: '#00C853' },
    RUNNING:  { label: 'Running',  color: '#F0B429', pulse: true },
    FAILED:   { label: 'Failed',   color: '#FF1F1F' },
    PENDING:  { label: 'Pending',  color: '#5A7A9A' },
  };
  const { label, color, pulse } = cfg[status] ?? cfg.PENDING;
  return (
    <span className="inline-flex items-center gap-1.5 text-[9px] font-mono font-bold">
      <span className="relative flex h-1.5 w-1.5">
        {pulse && (
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ background: color }}
          />
        )}
        <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: color }} />
      </span>
      <span style={{ color }}>{label}</span>
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[32, 220, 160, 70, 90, 180, 80, 80, 80].map((w, i) => (
        <td key={i} className="px-3 py-3" style={{ borderBottom: '1px solid #0F1928' }}>
          <div style={{ width: w, height: 10, background: '#182030', borderRadius: 4 }} />
        </td>
      ))}
    </tr>
  );
}

type IconComponent = React.ComponentType<LucideProps>;

function MetricCard({
  label, value, color, icon: Icon, subtext,
}: {
  label: string; value: number; color: string; icon: IconComponent; subtext?: string;
}) {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
      className="px-5 py-4 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>{label}</p>
        <Icon size={14} style={{ color }} />
      </div>
      <p className="text-2xl font-mono font-bold" style={{ color }}>{value.toLocaleString()}</p>
      {subtext && <p className="text-[9px] font-mono" style={{ color: '#3A5A7A' }}>{subtext}</p>}
    </div>
  );
}

type SortField = 'entity' | 'risk_tier' | 'created_at';
type SortDir = 'asc' | 'desc';

const TIER_ORDER: Record<RiskTier, number> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
const DATE_RANGES = [
  { label: 'Last 7d',  days: 7 },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'All time', days: 0 },
];
const TIERS: RiskTier[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const PAGE_SIZE = 25;

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CaseHistory() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<Set<RiskTier>>(new Set());
  const [typeFilter, setTypeFilter] = useState<'ALL' | CaseType>('ALL');
  const [dateRange, setDateRange]   = useState(0); // index into DATE_RANGES
  const [sortField, setSortField]   = useState<SortField>('created_at');
  const [sortDir, setSortDir]       = useState<SortDir>('desc');
  const [page, setPage]             = useState(0);
  const [selected, setSelected]     = useState<Set<string>>(new Set());

  // Debounce search 400ms
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  // Build API params
  const params: CaseListParams = useMemo(() => {
    const p: CaseListParams = { limit: 500 };
    if (debouncedSearch) p.search = debouncedSearch;
    if (typeFilter !== 'ALL') p.type = typeFilter;
    const range = DATE_RANGES[dateRange];
    if (range.days > 0) p.from = daysAgoISO(range.days);
    // We do tier filtering client-side (multi-select not supported by CaseListParams)
    return p;
  }, [debouncedSearch, typeFilter, dateRange]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['profiler-cases', params],
    queryFn: () => profilerApi.listCases(params),
    staleTime: 30_000,
  });

  const allCases = data?.cases ?? [];

  // Client-side tier filter
  const tierFiltered = useMemo(() => {
    if (tierFilter.size === 0) return allCases;
    return allCases.filter((c) => tierFilter.has(c.risk_tier));
  }, [allCases, tierFilter]);

  // Client-side sort
  const sorted = useMemo(() => {
    return [...tierFiltered].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'entity') {
        const ea = a.identifier_cluster?.canonical ?? a.seed ?? '';
        const eb = b.identifier_cluster?.canonical ?? b.seed ?? '';
        cmp = ea.localeCompare(eb);
      } else if (sortField === 'risk_tier') {
        cmp = (TIER_ORDER[a.risk_tier] ?? 0) - (TIER_ORDER[b.risk_tier] ?? 0);
      } else {
        cmp = new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime();
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [tierFiltered, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageSlice  = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const showFrom   = sorted.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const showTo     = Math.min((page + 1) * PAGE_SIZE, sorted.length);

  // Metric computations
  const now = Date.now();
  const weekAgo = now - 7 * 24 * 3600 * 1000;
  const metrics = useMemo(() => ({
    total:      tierFiltered.length,
    critical:   tierFiltered.filter((c) => c.risk_tier === 'CRITICAL').length,
    eddPending: tierFiltered.filter((c) => c.case_type === 'EDD' && (c.status === 'RUNNING' || c.status === 'PENDING')).length,
    thisWeek:   tierFiltered.filter((c) => c.created_at && new Date(c.created_at).getTime() >= weekAgo).length,
  }), [tierFiltered, weekAgo]);

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ChevronDown size={10} style={{ color: '#3A5A7A', opacity: 0.4 }} />;
    return sortDir === 'asc'
      ? <ChevronUp size={10} style={{ color: '#00C8FF' }} />
      : <ChevronDown size={10} style={{ color: '#00C8FF' }} />;
  }

  function toggleTier(tier: RiskTier) {
    setTierFilter((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier); else next.add(tier);
      return next;
    });
    setPage(0);
  }

  const allSelected = pageSlice.length > 0 && pageSlice.every((c) => selected.has(c.case_id));

  function toggleSelectAll() {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        pageSlice.forEach((c) => next.delete(c.case_id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pageSlice.forEach((c) => next.add(c.case_id));
        return next;
      });
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleExportAll() {
    const csv = buildCsv(sorted);
    downloadCsv(csv, `sycek-profiler-cases-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  function handleExportSelected() {
    const selectedCases = sorted.filter((c) => selected.has(c.case_id));
    const csv = buildCsv(selectedCases);
    downloadCsv(csv, `sycek-profiler-selected-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  function getModulesLabel(c: CDDReport): string {
    const mods: string[] = [
      ...(c.degraded_modules ?? []),
      ...(c.check_results?.map((r) => r.module) ?? []),
    ];
    const unique = [...new Set(mods)];
    if (unique.length === 0) return '—';
    if (unique.length <= 3) return unique.join(', ');
    return `${unique.slice(0, 3).join(', ')} +${unique.length - 3} more`;
  }

  const TIER_COLORS: Record<RiskTier, string> = {
    CRITICAL: '#FF1F1F', HIGH: '#FF6B1A', MEDIUM: '#F0B429', LOW: '#00C853',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ background: '#050709', minHeight: '100vh' }}
      className="flex flex-col"
    >
      {/* Header */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }} className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[13px] font-mono font-bold tracking-[0.22em]" style={{ color: '#DCE8F5' }}>
              CASE HISTORY
            </h1>
            <p className="text-[10px] font-mono mt-0.5" style={{ color: '#5A7A9A' }}>
              All profiler runs — CDD &amp; EDD
            </p>
          </div>
          <button
            onClick={handleExportAll}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all"
            style={{ background: '#0C1118', color: '#00C8FF', border: '1px solid #00C8FF30' }}
          >
            <Download size={12} />
            EXPORT CSV
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-5 px-6 py-6">

        {/* Filter bar */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="flex flex-wrap gap-3 items-center"
          style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 10, padding: '12px 16px' }}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} style={{ color: '#5A7A9A' }} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entity, case ref, client ref…"
              className="w-full pl-9 pr-3 py-2 rounded-lg font-mono text-[11px] outline-none transition-all"
              style={{ background: '#0C1118', border: '1px solid #182030', color: '#DCE8F5' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#1E2B3F'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#182030'; }}
            />
          </div>

          {/* Tier pills */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono tracking-widest mr-1" style={{ color: '#3A5A7A' }}>TIER</span>
            {TIERS.map((t) => {
              const active = tierFilter.has(t);
              const col = TIER_COLORS[t];
              return (
                <button
                  key={t}
                  onClick={() => toggleTier(t)}
                  className="px-2.5 py-0.5 rounded-full font-mono text-[9px] font-bold tracking-wider transition-all"
                  style={{
                    background: active ? `${col}18` : '#0C1118',
                    color: active ? col : '#5A7A9A',
                    border: `1px solid ${active ? `${col}44` : '#182030'}`,
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {/* Type toggle */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono tracking-widest mr-1" style={{ color: '#3A5A7A' }}>TYPE</span>
            {(['ALL', 'CDD', 'EDD'] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setTypeFilter(t); setPage(0); }}
                className="px-2.5 py-0.5 rounded-full font-mono text-[9px] font-bold tracking-wider transition-all"
                style={{
                  background: typeFilter === t ? '#00C8FF18' : '#0C1118',
                  color: typeFilter === t ? '#00C8FF' : '#5A7A9A',
                  border: `1px solid ${typeFilter === t ? '#00C8FF44' : '#182030'}`,
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono tracking-widest mr-1" style={{ color: '#3A5A7A' }}>DATE</span>
            {DATE_RANGES.map((r, i) => (
              <button
                key={r.label}
                onClick={() => { setDateRange(i); setPage(0); }}
                className="px-2.5 py-0.5 rounded-full font-mono text-[9px] font-bold tracking-wider transition-all"
                style={{
                  background: dateRange === i ? '#00C8FF18' : '#0C1118',
                  color: dateRange === i ? '#00C8FF' : '#5A7A9A',
                  border: `1px solid ${dateRange === i ? '#00C8FF44' : '#182030'}`,
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Metric cards */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <MetricCard label="TOTAL CASES"   value={metrics.total}      color="#DCE8F5"  icon={FileText}      subtext="in current filter" />
          <MetricCard label="CRITICAL"       value={metrics.critical}   color="#FF1F1F"  icon={AlertTriangle} subtext="highest risk" />
          <MetricCard label="EDD PENDING"    value={metrics.eddPending} color="#F0B429"  icon={Activity}      subtext="running or queued" />
          <MetricCard label="CREATED THIS WK" value={metrics.thisWeek}  color="#00C8FF"  icon={Calendar}      subtext="last 7 days" />
        </motion.div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 px-4 py-2.5 rounded-lg"
            style={{ background: '#0F1520', border: '1px solid #00C8FF30' }}
          >
            <span className="text-[10px] font-mono font-bold" style={{ color: '#00C8FF' }}>
              {selected.size} selected
            </span>
            <div className="h-3 w-px" style={{ background: '#182030' }} />
            <button
              onClick={handleExportSelected}
              className="flex items-center gap-1.5 text-[10px] font-mono font-bold transition-colors"
              style={{ color: '#DCE8F5' }}
            >
              <Download size={11} /> Export CSV
            </button>
            <button
              className="flex items-center gap-1.5 text-[10px] font-mono font-bold transition-colors"
              style={{ color: '#DCE8F5' }}
            >
              <Shield size={11} /> Add to Monitoring
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-[9px] font-mono"
              style={{ color: '#5A7A9A' }}
            >
              Clear
            </button>
          </motion.div>
        )}

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 10 }}
          className="overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #182030' }}>
                  {/* Checkbox */}
                  <th className="pl-4 pr-2 py-3 w-8">
                    <button onClick={toggleSelectAll} className="flex items-center justify-center">
                      {allSelected
                        ? <CheckSquare size={13} style={{ color: '#00C8FF' }} />
                        : <Square size={13} style={{ color: '#3A5A7A' }} />}
                    </button>
                  </th>
                  {/* Entity - sortable */}
                  <th
                    className="px-3 py-3 text-left cursor-pointer select-none"
                    style={{ width: 220, minWidth: 180 }}
                    onClick={() => toggleSort('entity')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>ENTITY</span>
                      <SortIcon field="entity" />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left" style={{ width: 160 }}>
                    <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>CASE REF</span>
                  </th>
                  <th className="px-3 py-3 text-left" style={{ width: 70 }}>
                    <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>TYPE</span>
                  </th>
                  {/* Tier - sortable */}
                  <th
                    className="px-3 py-3 text-left cursor-pointer select-none"
                    style={{ width: 90 }}
                    onClick={() => toggleSort('risk_tier')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>TIER</span>
                      <SortIcon field="risk_tier" />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left" style={{ minWidth: 160 }}>
                    <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>MODULES</span>
                  </th>
                  {/* When - sortable */}
                  <th
                    className="px-3 py-3 text-left cursor-pointer select-none"
                    style={{ width: 90 }}
                    onClick={() => toggleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>WHEN</span>
                      <SortIcon field="created_at" />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left" style={{ width: 80 }}>
                    <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>STATUS</span>
                  </th>
                  <th className="px-3 py-3 text-right" style={{ width: 70 }}>
                    <span className="text-[9px] font-mono tracking-[0.14em]" style={{ color: '#5A7A9A' }}>ACTION</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : isError ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <p className="text-[11px] font-mono" style={{ color: '#FF1F1F' }}>Failed to load cases.</p>
                    </td>
                  </tr>
                ) : pageSlice.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-16 text-center">
                      <Inbox size={32} style={{ color: '#182030', margin: '0 auto 12px' }} />
                      <p className="text-[12px] font-mono font-bold mb-2" style={{ color: '#3A5A7A' }}>No cases found</p>
                      <Link
                        to="/app/profiler"
                        className="inline-flex items-center gap-1.5 text-[10px] font-mono transition-colors"
                        style={{ color: '#00C8FF' }}
                      >
                        Run your first check <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                ) : (
                  pageSlice.map((c) => {
                    const entity = c.identifier_cluster?.canonical ?? c.seed ?? c.case_reference ?? '—';
                    const caseType = (c as unknown as { case_type?: string }).case_type ?? 'CDD';
                    const href = caseType === 'EDD' && (c.status === 'COMPLETE' || c.status === 'RUNNING')
                      ? `/app/profiler/case/${c.case_id}/edd`
                      : `/app/profiler/case/${c.case_id}/cdd`;
                    const isSelected = selected.has(c.case_id);
                    return (
                      <tr
                        key={c.case_id}
                        onClick={() => navigate(href)}
                        className="cursor-pointer transition-colors hover:bg-white/[0.025]"
                        style={{
                          borderBottom: '1px solid #0F1928',
                          background: isSelected ? '#00C8FF08' : undefined,
                        }}
                      >
                        {/* Checkbox */}
                        <td
                          className="pl-4 pr-2 py-3"
                          onClick={(e) => { e.stopPropagation(); toggleSelect(c.case_id); }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(c.case_id)}
                            className="border-[#182030] data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                          />
                        </td>
                        {/* Entity */}
                        <td className="px-3 py-3" style={{ maxWidth: 220 }}>
                          <span
                            className="block font-mono text-[11px] font-semibold truncate"
                            style={{ color: '#DCE8F5' }}
                            title={entity}
                          >
                            {entity}
                          </span>
                          {c.client_ref && (
                            <span className="font-mono text-[9px]" style={{ color: '#3A5A7A' }}>{c.client_ref}</span>
                          )}
                        </td>
                        {/* Case ref */}
                        <td className="px-3 py-3">
                          <span className="font-mono text-[10px]" style={{ color: '#5A7A9A', letterSpacing: '0.05em' }}>
                            {c.case_reference ?? c.case_id.slice(0, 16)}
                          </span>
                        </td>
                        {/* Type */}
                        <td className="px-3 py-3">
                          <CaseTypeBadge type={c.case_type} />
                        </td>
                        {/* Tier */}
                        <td className="px-3 py-3">
                          <RiskTierBadge tier={c.risk_tier} />
                        </td>
                        {/* Modules */}
                        <td className="px-3 py-3">
                          <span className="font-mono text-[9px]" style={{ color: '#3A5A7A' }}>
                            {getModulesLabel(c)}
                          </span>
                        </td>
                        {/* When */}
                        <td className="px-3 py-3">
                          <span className="flex items-center gap-1 font-mono text-[9px]" style={{ color: '#3A5A7A' }}>
                            <Clock size={9} />
                            {relativeTime(c.created_at)}
                          </span>
                        </td>
                        {/* Status */}
                        <td className="px-3 py-3">
                          <StatusChip status={c.status} />
                        </td>
                        {/* View */}
                        <td className="px-3 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(href); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded font-mono text-[9px] font-bold tracking-wider transition-all"
                            style={{ background: '#0C1118', color: '#00C8FF', border: '1px solid #00C8FF22' }}
                          >
                            <Eye size={10} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!isLoading && sorted.length > 0 && (
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ borderTop: '1px solid #182030' }}
            >
              <span className="text-[10px] font-mono" style={{ color: '#5A7A9A' }}>
                Showing {showFrom}–{showTo} of {sorted.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 rounded font-mono text-[10px] font-bold tracking-wider transition-all disabled:opacity-30"
                  style={{ background: '#0C1118', color: '#DCE8F5', border: '1px solid #182030' }}
                >
                  Prev
                </button>
                <span className="text-[10px] font-mono" style={{ color: '#5A7A9A' }}>
                  {page + 1} / {Math.max(1, totalPages)}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 rounded font-mono text-[10px] font-bold tracking-wider transition-all disabled:opacity-30"
                  style={{ background: '#0C1118', color: '#DCE8F5', border: '1px solid #182030' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </motion.div>

      </div>
    </motion.div>
  );
}
